import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';
import { escapeRegExp } from 'lodash';
import { useTheme } from 'emotion-theming';
import { css } from '@emotion/core';
import Link from '@veeva/corgix/link';
import getLastLoginContent from '../../services/getLastLoginContent';
import buildAndSubmitForm from '../../services/buildAndSubmitForm';
import { isMobile } from '../../services/DeviceInformation';
import mobileContentWidthCSS from '../../constants/mobileContentWidthCSS';
import StudiesGrid from './StudiesGrid';
import getTargetAttribute from '../../services/getTargetAttribute';

const StudiesSection = ({ section, language, searchTerm, showingStudiesLabel }) => {
    const { lighterGray, silverLighter, silverDark } = useTheme();

    const { companyName, lastLogin, lastLoginFormatted, redirectUrl, studies } = section;
    const [filteredStudies, setFilteredStudies] = useState(studies);

    // Filter studies as long as search term does not match section companyName
    useEffect(() => {
        if (searchTerm && !companyName.toLowerCase()?.includes(searchTerm?.toLowerCase())) {
            const newFilteredStudies = studies?.filter((study) => {
                return study?.studyName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
            });

            setFilteredStudies(newFilteredStudies);
        } else {
            setFilteredStudies(studies);
        }
    }, [searchTerm, studies, companyName]);

    const filteredStudiesCount =
        filteredStudies.length === studies.length
            ? ''
            : ` (${showingStudiesLabel
                  .replace('{0}', filteredStudies.length)
                  .replace('{1}', studies.length)})`;

    const handleLinkClick = (e) => {
        e.preventDefault();
        buildAndSubmitForm(redirectUrl, {}, { openInNewTab: true });
    };

    const escapedSearchTerm = escapeRegExp(searchTerm);

    const sectionTitle = (
        <Highlighter
            searchWords={[escapedSearchTerm]}
            textToHighlight={companyName}
            highlightTag="strong"
        />
    );

    return (
        <div
            css={[
                css`
                    border-radius: 8px;
                    border: solid 1px ${silverDark};
                    margin-bottom: 1rem;
                `,
                isMobile() &&
                    css`
                        ${mobileContentWidthCSS}
                    `,
            ]}
            data-target={getTargetAttribute('studies-section')}
        >
            <div
                css={css`
                    background-color: ${silverLighter};
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    font-size: 1.1666rem;
                    font-style: italic;
                    color: ${lighterGray};
                    padding: 1rem;
                    gap: 0.3333rem;
                `}
            >
                {redirectUrl ? (
                    <span data-target={getTargetAttribute('section-title-link')}>
                        <Link
                            css={css`
                                font-size: 1.1666rem;
                                font-style: normal;
                                overflow-wrap: break-word;
                            `}
                            onClick={handleLinkClick}
                            href={redirectUrl}
                        >
                            {sectionTitle}
                            {filteredStudiesCount}
                        </Link>
                    </span>
                ) : (
                    <span data-target={getTargetAttribute('section-title')}>
                        {sectionTitle}
                        {filteredStudiesCount}
                    </span>
                )}
                {getLastLoginContent(lastLogin, lastLoginFormatted, language)}
            </div>
            <StudiesGrid studies={filteredStudies} searchTerm={searchTerm} />
        </div>
    );
};

StudiesSection.propTypes = {
    language: PropTypes.string,
    searchTerm: PropTypes.string,
    section: PropTypes.shape({
        companyName: PropTypes.string,
        lastLogin: PropTypes.instanceOf(Date),
        lastLoginFormatted: PropTypes.string,
        redirectUrl: PropTypes.string,
        studies: PropTypes.arrayOf(
            PropTypes.shape({
                redirectUrl: PropTypes.string,
                studyName: PropTypes.string,
            }),
        ),
    }),
    showingStudiesLabel: PropTypes.string,
};

export default StudiesSection;
